import { css } from '@emotion/react'
import api from 'api'
import { ImpressaoAcompanhamentoMChatInput } from 'api/ImpressaoMChat'
import { Button, Icon, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { reportFormatDate, useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import { Fragment, useMemo, useState } from 'react'
import { RegistroMChatAcompanhamentoPuericultura } from 'view/atendimentos/detail/soap/aside/acompanhamento-puericultura/model'

import { ImpressaoConsultaSeguimento } from '../consulta-seguimento/model-consultaSeguimento'
import { ImpressaoMchat } from '../mchat/model-mchat'
import { buildConsultaSeguimentoStats } from '../utils-mChatRf'
import { ConsultaSeguimentoRegistroListItem } from './ConsultaSeguimentoRegistroListItem'
import { MChatRegistroListItem } from './MChatRegistroListItem'

interface MChatRfRegistrosListProps {
  items: ReadonlyArray<RegistroMChatAcompanhamentoPuericultura>
  cidadaoId: ID
  cidadaoDataNascimento: LocalDate
}

const INITIAL_LIMIT = 3
const SHOW_ALL_THRESHOLD = 4

export function MChatRfRegistrosList(props: Readonly<MChatRfRegistrosListProps>) {
  const { items, cidadaoId, cidadaoDataNascimento } = props

  const theme = useTheme()
  const { handleImpressaoPath, handleImpressaoError } = useHandleImpressao<ImpressaoAcompanhamentoMChatInput>()
  const [isExpanded, setIsExpanded] = useState(false)

  const totalItems = items.length
  const shouldShowToggle = totalItems > SHOW_ALL_THRESHOLD
  const displayedItems = shouldShowToggle && !isExpanded ? items.slice(0, INITIAL_LIMIT) : items
  const remainingCount = totalItems - INITIAL_LIMIT

  const styles = useMemo(() => createStyles(theme, isExpanded, shouldShowToggle), [isExpanded, shouldShowToggle, theme])

  const handleToggle = () => {
    setIsExpanded((prev) => !prev)
  }

  const handlePrint = (event: React.MouseEvent<HTMLButtonElement>, item: RegistroMChatAcompanhamentoPuericultura) => {
    event.stopPropagation()

    const seguimentoStats = buildConsultaSeguimentoStats(item)

    const inputR: ImpressaoMchat = {
      atendProfId: item.atendimentoProfissional.id,
      cidadaoId: cidadaoId,
      resultado: item.pontuacao,
      risco: item.risco,
      aplicacao: item.aplicacao,
    }

    const inputSeguimento: ImpressaoConsultaSeguimento = {
      atendProfId: item.atendimentoProfissional.id,
      cidadaoId: cidadaoId,
      resultadoSeguimento: seguimentoStats.resultado,
      totalPerguntasRespondidas: seguimentoStats.totalPerguntasRespondidas,
      totalPerguntasFalhou: seguimentoStats.totalPerguntasFalhou,
      totalPerguntasPassou: seguimentoStats.totalPerguntasPassou,
      aplicacaoRf: seguimentoStats.aplicacao,
    }

    handleImpressaoPath({
      apiPath: api.mchat.imprimirMChat,
      values: item.isMchatRf ? inputSeguimento : inputR,
      file: `MChat.pdf`,
      dateFormat: reportFormatDate,
    }).catch(handleImpressaoError)
  }

  if (totalItems === 0) {
    return (
      <Box style={styles.emptyBox}>
        <Text color='secondary' fontStyle='italic'>
          Sem registros de M-CHAT-R/F™
        </Text>
      </Box>
    )
  }

  return (
    <Box style={styles.box}>
      <VFlow style={styles.innerBox}>
        {displayedItems.map((item, index) => (
          <Fragment key={item.id}>
            {item.isMchatRf && (
              <ConsultaSeguimentoRegistroListItem
                item={item}
                cidadaoDataNascimento={cidadaoDataNascimento}
                isLastItem={index === displayedItems.length - 1}
                onPrint={handlePrint}
              />
            )}
            {!item.isMchatRf && (
              <MChatRegistroListItem
                item={item}
                cidadaoDataNascimento={cidadaoDataNascimento}
                isLastItem={index === displayedItems.length - 1}
                onPrint={handlePrint}
              />
            )}
          </Fragment>
        ))}
      </VFlow>
      {shouldShowToggle && (
        <Button skin='ghost' size='small' onClick={handleToggle} style={styles.toggleButton}>
          <Icon icon='angleDown' size={1.5} />
          {isExpanded ? `Ocultar +` : `Mostrar +`}
          {'resultado'.pluralizeAndConcatValue(remainingCount)}
        </Button>
      )}
    </Box>
  )
}

const createStyles = (theme: Theme, isExpanded: boolean, shouldShowToggle: boolean) => ({
  box: css`
    padding: 0px;
  `,
  emptyBox: css`
    text-align: center;
  `,
  innerBox: css`
    padding: 1rem;
    border-bottom: ${shouldShowToggle ? `1px solid ${theme.pallete.divider}` : '0'};
  `,
  toggleButton: css`
    width: 100%;
    justify-content: flex-start;
    border-radius: 0;
    & svg {
      margin-right: 0.5rem;
      transform: ${isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
      transition: '0.4s';
    }
  `,
})
